import {
  AppRole,
  ResourceIdentityType,
  ResponsibleParty,
} from '../../../../insights/types/RoleTypes';
import { DocumentNode } from 'graphql/index';
import {
  BaseApplicationProperty,
  MutationStatus,
} from '../updateApplication/BaseApplicationProperty';
import { gql } from '@apollo/client/core';

/**
 * This mutation is responsible for creating responsibilities
 */
export const CreateResponsibilities = gql`
  mutation CreateResponsibilities($input: [CreateResponsibilityInput!]!) {
    resources {
      responsibilities {
        createResponsibilities(input: $input) {
          applicationId
          success
          message
        }
      }
    }
  }
`;

/**
 * This type is responsible for the result of the CreateResponsibilities mutation
 */
export type CreateResponsibilitiesMutationResult = {
  resources: {
    responsibilities: {
      createResponsibilities: MutationStatus[];
    };
  };
};

/**
 * This class is responsible for managing the Role property of the application
 */
export class RoleProperty implements BaseApplicationProperty {
  field: string;
  value?: {
    role: AppRole;
    party: ResponsibleParty;
    identityType: ResourceIdentityType;
  };
  label: string = 'Role';

  constructor(field: string) {
    this.field = field;
  }

  /**
   * Sets the value of the RoleProperty.
   * @param value - The value to set, containing role, party, and identityType.
   */
  set(value: {
    role: AppRole;
    party: ResponsibleParty;
    identityType: ResourceIdentityType;
  }) {
    this.value = value;
  }

  /**
   * Generates the input for the property mutation.
   * @returns An object containing identityType, identityId, and roleId.
   */
  propertyMutationInput(): {
    identityType: ResourceIdentityType;
    identityId: string;
    roleId: number;
  } {
    return {
      identityType: this.value?.identityType ?? ResourceIdentityType.User,
      identityId: this.value?.party.login || this.value?.party.name || '',
      roleId: this.value?.role.id || 0,
    };
  }
  /**
   * Generates a display value for the RoleProperty.
   * @returns A string containing the role display name and identity.
   */
  getNewDisplayValue(): string {
    return `{ role: ${this.value?.role.displayName}, identity: ${
      this.value?.party.login || this.value?.party.name || ''
    } }`;
  }
  /**
   * Returns the GraphQL mutation query for creating responsibilities.
   * @returns The GraphQL mutation query document.
   */
  mutationQuery(): DocumentNode {
    return CreateResponsibilities;
  }
  /**
   * Extracts the mutation result from the response data.
   * @param data - The result data from the CreateResponsibilities mutation.
   * @returns An array of MutationStatus objects.
   */
  getMutationResult(data: CreateResponsibilitiesMutationResult) {
    return data?.resources?.responsibilities?.createResponsibilities;
  }
}
